// INCIDENT COMMAND WORKSPACE — major-event ICS board: seats, objectives,
// ICS-214 situation log, linked CAD incidents and resource requests.
import React, { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Command, Plus, Link2, PackagePlus } from "lucide-react";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const SEATS = [["incident_commander", "INCIDENT COMMANDER"], ["operations", "OPERATIONS"], ["planning", "PLANNING"],
  ["logistics", "LOGISTICS"], ["safety_officer", "SAFETY OFFICER"], ["pio", "PIO"]];
const TYPES = ["major_incident", "planned_event", "disaster", "search_rescue", "civil_disturbance", "exercise"];
const OBJ_COLOR = { open: "#F59E0B", in_progress: "#3B82F6", complete: "#22C55E" };
const RES_COLOR = { requested: "#F59E0B", ordered: "#3B82F6", on_scene: "#22C55E", released: "#9CA3AF", cancelled: "#6B7280" };

export function IcsEventBoard() {
  const [events, setEvents] = useState([]);
  const [sel, setSel] = useState(null); // full event doc
  const [form, setForm] = useState({ name: "", type: "major_incident", description: "", staging: "", radio_channel: "" });
  const [logEntry, setLogEntry] = useState("");
  const [objText, setObjText] = useState("");
  const [linkCc, setLinkCc] = useState("");
  const [resForm, setResForm] = useState({ item: "", qty: "1", priority: "routine" });

  const loadList = useCallback(() => client.get("/command/events").then((r) => setEvents(r.data.events || [])).catch(() => {}), []);
  const loadSel = useCallback((eid) => client.get(`/command/events/${eid}`).then((r) => setSel(r.data)).catch(() => {}), []);
  useEffect(() => { loadList(); const iv = setInterval(loadList, 15000); return () => clearInterval(iv); }, [loadList]);
  useEffect(() => { if (!sel?.event_id) return; const iv = setInterval(() => loadSel(sel.event_id), 10000); return () => clearInterval(iv); }, [sel?.event_id, loadSel]);

  const err = (e, msg) => toast.error(e.response?.data?.detail || msg);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Event name required"); return; }
    try {
      const { data } = await client.post("/command/events", form);
      toast.success(`Command event ${data.event_id} activated`);
      setForm({ name: "", type: "major_incident", description: "", staging: "", radio_channel: "" });
      loadList(); setSel(data);
    } catch (e) { err(e, "Could not create event"); }
  };
  const setSeat = async (seat, name) => {
    try { await client.patch(`/command/events/${sel.event_id}`, { ics: { [seat]: name } }); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not assign seat"); }
  };
  const addLog = async () => {
    if (!logEntry.trim()) return;
    try { await client.post(`/command/events/${sel.event_id}/log`, { entry: logEntry.trim() }); setLogEntry(""); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not add log entry"); }
  };
  const addObj = async () => {
    if (!objText.trim()) return;
    try { await client.post(`/command/events/${sel.event_id}/objectives`, { text: objText.trim() }); setObjText(""); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not add objective"); }
  };
  const objStatus = async (o, status) => {
    try { await client.patch(`/command/events/${sel.event_id}/objectives/${o.id}`, { status }); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not update objective"); }
  };
  const link = async () => {
    if (!linkCc.trim()) return;
    try { await client.post(`/command/events/${sel.event_id}/link-incident`, { cc: linkCc.trim() }); setLinkCc(""); toast.success("CAD incident linked"); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not link incident"); }
  };
  const unlink = async (cc) => {
    try { await client.post(`/command/events/${sel.event_id}/unlink-incident`, { cc }); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not unlink"); }
  };
  const addRes = async () => {
    if (!resForm.item.trim()) return;
    try { await client.post(`/command/events/${sel.event_id}/resources`, resForm); setResForm({ item: "", qty: "1", priority: "routine" }); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not request resource"); }
  };
  const resStatus = async (r, status) => {
    try { await client.patch(`/command/events/${sel.event_id}/resources/${r.id}`, { status }); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not update resource"); }
  };
  const demobilize = async () => {
    if (!window.confirm(`Demobilize ${sel.name}? The event closes but its record is preserved.`)) return;
    try { await client.patch(`/command/events/${sel.event_id}`, { status: "demobilized" }); toast(`${sel.event_id} DEMOBILIZED`); loadList(); loadSel(sel.event_id); }
    catch (e) { err(e, "Could not demobilize"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-3" data-testid="ics-board">
      <div className="space-y-3">
        <div className="border border-[#2C2D35] rounded-sm p-3 space-y-2" data-testid="ics-create-form">
          <div className="font-head text-xs font-semibold flex items-center gap-2"><Command size={13} className="text-[#DC2626]" /> ACTIVATE COMMAND EVENT</div>
          <input data-testid="ics-name-input" className={`${inp} w-full`} placeholder="Event name (e.g. Riverfront Flood Response)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="flex gap-2">
            <select data-testid="ics-type-select" className={`${inp} flex-1`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((tp) => <option key={tp} value={tp}>{tp.replace(/_/g, " ").toUpperCase()}</option>)}
            </select>
            <input data-testid="ics-channel-input" className={`${inp} w-24`} placeholder="TAC ch." value={form.radio_channel} onChange={(e) => setForm({ ...form, radio_channel: e.target.value })} />
          </div>
          <input data-testid="ics-staging-input" className={`${inp} w-full`} placeholder="Staging area" value={form.staging} onChange={(e) => setForm({ ...form, staging: e.target.value })} />
          <textarea data-testid="ics-desc-input" className={`${inp} w-full`} rows={2} placeholder="Situation summary…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button data-testid="ics-create-btn" onClick={create} className="w-full py-2 rounded-sm font-head font-bold text-xs text-white" style={{ background: "#DC2626" }}>ACTIVATE</button>
        </div>
        <div className="border border-[#2C2D35] rounded-sm" data-testid="ics-events-list">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">COMMAND EVENTS ({events.length})</div>
          {events.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No command events.</div>}
          {events.map((ev) => (
            <button key={ev.event_id} data-testid={`ics-event-${ev.event_id}`} onClick={() => loadSel(ev.event_id)}
              className="w-full text-left px-3 py-2 border-b border-[#1E1F24] hover:bg-[#15161b]"
              style={sel?.event_id === ev.event_id ? { background: "#15161b", borderLeft: "3px solid #DC2626" } : {}}>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: ev.status === "active" ? "#22C55E" : "#6B7280" }} />
                <span className="text-white text-xs font-semibold truncate">{ev.name}</span>
              </div>
              <div className="font-mono text-[9px] text-[#6B7280] mt-0.5">{ev.event_id} · {(ev.type || "").replace(/_/g, " ").toUpperCase()} · {ev.status.toUpperCase()} · {(ev.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </button>
          ))}
        </div>
      </div>

      {!sel ? (
        <div className="border border-[#2C2D35] rounded-sm flex items-center justify-center min-h-[300px] font-mono text-[11px] text-[#6B7280]" data-testid="ics-empty">
          Select or activate a command event to open its workspace.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ics-workspace">
          <div className="border border-[#2C2D35] rounded-sm p-3 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-display font-black text-lg text-white uppercase">{sel.name}</div>
              <div className="font-mono text-[10px] text-[#9CA3AF]">{sel.event_id} · {(sel.type || "").replace(/_/g, " ").toUpperCase()} · {sel.radio_channel && `RADIO ${sel.radio_channel} · `}{sel.staging && `STAGING: ${sel.staging} · `}by {sel.created_by}</div>
              {sel.description && <div className="font-mono text-[10px] text-white mt-1">{sel.description}</div>}
            </div>
            <span className="font-head text-[10px] font-bold px-2 py-1 rounded" style={{ background: sel.status === "active" ? "#22C55E" : "#6B7280", color: "#0A0A0A" }}>{sel.status.toUpperCase()}</span>
            {sel.status === "active" && (
              <button data-testid="ics-demobilize-btn" onClick={demobilize} className="px-3 py-1.5 rounded font-head font-bold text-[10px] text-white" style={{ background: "#7F1D1D" }}>DEMOBILIZE</button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-[#2C2D35] rounded-sm" data-testid="ics-seats">
              <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">ICS COMMAND STRUCTURE</div>
              {SEATS.map(([seat, lab]) => (
                <div key={seat} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E1F24]">
                  <span className="font-mono text-[9px] text-[#9CA3AF] w-40">{lab}</span>
                  <input data-testid={`ics-seat-${seat}`} className={`${inp} flex-1 py-1`} defaultValue={sel.ics?.[seat] || ""} key={`${sel.event_id}-${seat}-${sel.ics?.[seat]}`}
                    placeholder="Unassigned" onBlur={(e) => { if (e.target.value !== (sel.ics?.[seat] || "")) setSeat(seat, e.target.value); }} />
                </div>
              ))}
            </div>

            <div className="border border-[#2C2D35] rounded-sm" data-testid="ics-objectives">
              <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">INCIDENT OBJECTIVES</div>
              <div className="flex gap-2 p-2 border-b border-[#1E1F24]">
                <input data-testid="ics-obj-input" className={`${inp} flex-1 py-1`} placeholder="New objective…" value={objText} onChange={(e) => setObjText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addObj()} />
                <button data-testid="ics-obj-add" onClick={addObj} className="px-2.5 rounded font-head font-bold text-xs text-black" style={{ background: "#F59E0B" }}><Plus size={13} /></button>
              </div>
              {(sel.objectives || []).map((o) => (
                <div key={o.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E1F24]" data-testid={`ics-obj-${o.id}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: OBJ_COLOR[o.status] }} />
                  <span className="font-mono text-[10px] text-white flex-1" style={o.status === "complete" ? { textDecoration: "line-through", opacity: 0.6 } : {}}>{o.text}</span>
                  <select data-testid={`ics-obj-status-${o.id}`} className={`${inp} py-0.5 text-[9px]`} value={o.status} onChange={(e) => objStatus(o, e.target.value)}>
                    {["open", "in_progress", "complete"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="border border-[#2C2D35] rounded-sm" data-testid="ics-linked">
              <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2"><Link2 size={12} className="text-[#22D3EE]" /> LINKED CAD INCIDENTS</div>
              <div className="flex gap-2 p-2 border-b border-[#1E1F24]">
                <input data-testid="ics-link-input" className={`${inp} flex-1 py-1`} placeholder="CC number (e.g. CC-2026-000123)" value={linkCc} onChange={(e) => setLinkCc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && link()} />
                <button data-testid="ics-link-add" onClick={link} className="px-2.5 rounded font-head font-bold text-xs text-black" style={{ background: "#22D3EE" }}>LINK</button>
              </div>
              {(sel.linked_incidents || []).length === 0 && <div className="p-2.5 font-mono text-[10px] text-[#6B7280]">No CAD incidents attached.</div>}
              {(sel.linked_incidents || []).map((cc) => (
                <div key={cc} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E1F24]">
                  <span className="font-mono text-[10px] text-[#22D3EE] flex-1">{cc}</span>
                  <button data-testid={`ics-unlink-${cc}`} onClick={() => unlink(cc)} className="font-head text-[9px] font-bold text-[#EF4444]">UNLINK</button>
                </div>
              ))}
            </div>

            <div className="border border-[#2C2D35] rounded-sm" data-testid="ics-resources">
              <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2"><PackagePlus size={12} className="text-[#F97316]" /> RESOURCE REQUESTS</div>
              <div className="flex gap-2 p-2 border-b border-[#1E1F24] flex-wrap">
                <input data-testid="ics-res-item" className={`${inp} flex-1 min-w-[140px] py-1`} placeholder="Resource (e.g. light tower)" value={resForm.item} onChange={(e) => setResForm({ ...resForm, item: e.target.value })} />
                <input data-testid="ics-res-qty" className={`${inp} w-14 py-1`} placeholder="Qty" value={resForm.qty} onChange={(e) => setResForm({ ...resForm, qty: e.target.value })} />
                <select data-testid="ics-res-priority" className={`${inp} py-1`} value={resForm.priority} onChange={(e) => setResForm({ ...resForm, priority: e.target.value })}>
                  <option value="routine">ROUTINE</option><option value="urgent">URGENT</option><option value="immediate">IMMEDIATE</option>
                </select>
                <button data-testid="ics-res-add" onClick={addRes} className="px-2.5 rounded font-head font-bold text-xs text-black" style={{ background: "#F97316" }}>REQUEST</button>
              </div>
              {(sel.resources || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E1F24]" data-testid={`ics-res-${r.id}`}>
                  <span className="font-mono text-[10px] text-white flex-1">{r.qty}× {r.item} <span className="text-[#6B7280]">({r.priority})</span></span>
                  <select data-testid={`ics-res-status-${r.id}`} className={`${inp} py-0.5 text-[9px]`} value={r.status} onChange={(e) => resStatus(r, e.target.value)}
                    style={{ borderColor: RES_COLOR[r.status] }}>
                    {["requested", "ordered", "on_scene", "released", "cancelled"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[#2C2D35] rounded-sm" data-testid="ics-log">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">SITUATION LOG (ICS-214)</div>
            <div className="flex gap-2 p-2 border-b border-[#1E1F24]">
              <input data-testid="ics-log-input" className={`${inp} flex-1 py-1`} placeholder="Log a command decision / situation update…" value={logEntry} onChange={(e) => setLogEntry(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLog()} />
              <button data-testid="ics-log-add" onClick={addLog} className="px-3 rounded font-head font-bold text-xs text-white" style={{ background: "#3B82F6" }}>LOG</button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {[...(sel.log || [])].reverse().map((l) => (
                <div key={l.id} className="px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[10px]">
                  <span className="text-[#6B7280]">{(l.ts || "").slice(5, 16).replace("T", " ")} · {l.by} · {(l.category || "").toUpperCase()}</span>
                  <div className="text-white">{l.entry}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
